/**
 * Custom Search with Shopify Fallback
 *
 * Features:
 * - Calls custom API endpoint for search results
 * - 5 second timeout protection
 * - Automatic fallback to Shopify search on error
 * - Renders products in theme-styled grid (4 columns)
 * - Shows prices with sale badges
 * - Fully responsive (mobile/tablet/desktop)
 * - XSS protection
 *
 * Flow:
 * 1. User searches -> API called
 * 2. If API succeeds -> Show custom results
 * 3. If API fails/timeout -> Show Shopify results (seamless fallback)
 */

const SearchAI_SearchId = 'srch_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);

(function () {
  'use strict';

  // Configuration
  const CONFIG = {
    apiEndpoint: 'https://private.api.searchaiengine.com/shopify/search-track',
    timeout: 6000, // 6 seconds
    testMode: false, // Set to true to use static test data instead of calling API
    abTestEnabled: true // Set to false to disable A/B testing
  };

  const CustomSearch = {

    /**
     * Check if should use AI search based on A/B test
     */
    shouldUseAI: function () {
      if (!CONFIG.abTestEnabled) {
        console.log('%c[A/B TEST] A/B testing DISABLED - Using AI', 'background: #607D8B; color: white; padding: 3px;');
        return true;
      }

      if (!window.SearchAI?.Session?.isAISearchGroup) {
        console.log('%c[A/B TEST] ⚠️ SearchAI not available - Defaulting to AI', 'background: #FF5722; color: white; padding: 3px;');
        return true;
      }

      const useAI = window.SearchAI.Session.isAISearchGroup();
      console.log(`%c[A/B TEST] 🔀 Routing to: ${useAI ? 'AI SEARCH' : 'SHOPIFY SEARCH'}`, `background: ${useAI ? '#2196F3' : '#FF9800'}; color: white; padding: 5px; font-weight: bold;`);
      return useAI;
    },

    /**
     * Initialize
     */
    init: function () {
      console.log('[CustomSearch] Initializing...');

      // Get search query from URL
      const urlParams = new URLSearchParams(window.location.search);
      const query = urlParams.get('q');

      if (query && query.trim().length > 0) {
        console.log('[CustomSearch] Search query found:', query);
        this.executeSearch(query.trim());
      } else {
        console.log('[CustomSearch] No search query');
      }
    },

    /**
     * Execute search
     */
    executeSearch: async function (query) {
      this.isErrorFallback = false; // Reset fallback status
      console.log('[CustomSearch] Starting search for:', query);

      // Check A/B test group
      if (!this.shouldUseAI()) {
        console.log('%c[CustomSearch] 🚦 REDIRECTING TO SHOPIFY (A/B test)', 'background: #FF9800; color: white; padding: 5px; font-weight: bold;');
        this.fallbackToShopify();
        // Report analytics after Shopify renders (1 sec delay)
        const shopifyResults = this.getShopifyProductIds();
        reportSearchToAnalytics({ results: shopifyResults }, query, 0);
        return;
      }

      console.log('%c[CustomSearch] 🚀 Using AI SEARCH', 'background: #2196F3; color: white; padding: 5px; font-weight: bold;');

      try {
        // Call custom API
        const apiResults = await this.callCustomAPI(query);

        if (apiResults && apiResults.performed && apiResults.results) {
          console.log('[CustomSearch] ✓ API Success! Got', apiResults.resultsCount, 'results');
          // -------------- Report Search To Analytics -----------------------
          reportSearchToAnalytics(apiResults, query, 1);
          // -----------------------------------------------------------------
          // Show blank page for now (will add rendering in next step)
          this.showBlankPage(apiResults);
        } else {
          throw new Error('Invalid API response');
        }

      } catch (error) {
        console.log('[CustomSearch] ✗ API Failed:', error.message);
        console.log('%c[CustomSearch] 🚦 REDIRECTING TO SHOPIFY (API error)', 'background: #F44336; color: white; padding: 5px; font-weight: bold;');

        // Fallback to Shopify
        this.isErrorFallback = true;
        this.fallbackToShopify();

        // Skip analytics reporting for error-based fallback
        console.log('[CustomSearch] ℹ️ Error fallback marked, skipping analytics reporting.');
      }
    },

    /**
     * Call custom API with timeout (or use test data if testMode enabled)
     */
    callCustomAPI: function (query) {
      // TEST MODE: Return static test data
      if (CONFIG.testMode) {
        console.log('[CustomSearch] 🧪 TEST MODE - Using static test data');
        return this.getTestData(query);
      }

      // PRODUCTION MODE: Call real API
      console.log('[CustomSearch] Calling API...');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CONFIG.timeout);

      return fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: {
          'Accept': '*/*',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'Accept-Language': 'en-US,en;q=0.9',
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
          'Referer': window.location.href,
          'User-Agent': navigator.userAgent,
          'ngrok-skip-browser-warning': '1'
        },
        body: JSON.stringify({
          query: query,
          url: window.location.href,
          timestamp: new Date().toISOString(),
          language: Shopify.locale || 'en',
          currency: Shopify.country || 'AD',
          sortBy: 'relevance',
          filters: {} // Empty filters for initial search
        }),
        signal: controller.signal
      })
        .then(response => {
          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }

          return response.json();
        })
        .catch(error => {
          clearTimeout(timeoutId);

          if (error.name === 'AbortError') {
            throw new Error('API timeout');
          }

          throw error;
        });
    },

    /**
     * Get test data (for testing without API) - Matches exact API response format
     */
    getTestData: function (query) {
      return Promise.resolve({
        "defaultSortBy": "relevance",
        "filters": [
          {
            "presentation": "text",
            "url_to_remove": "",
            "values": [
              { "count": 9, "active": false, "label": "NERO", "value": "NERO", "param_name": "filter.v.option.color" },
              { "count": 3, "active": false, "label": "BLU NAVY", "value": "BLU NAVY", "param_name": "filter.v.option.color" },
              { "count": 2, "active": false, "label": "FANGO", "value": "FANGO", "param_name": "filter.v.option.color" },
              { "count": 1, "active": false, "label": "MARRONE", "value": "MARRONE", "param_name": "filter.v.option.color" },
              { "count": 1, "active": false, "label": "SILVER", "value": "SILVER", "param_name": "filter.v.option.color" },
              { "count": 1, "active": false, "label": "BLACK", "value": "BLACK", "param_name": "filter.v.option.color" },
              { "count": 1, "active": false, "label": "BORDEAUX", "value": "BORDEAUX", "param_name": "filter.v.option.color" },
              { "count": 1, "active": false, "label": "INDIGO", "value": "INDIGO", "param_name": "filter.v.option.color" }
            ],
            "label": "Color",
            "active_values": [],
            "type": "list",
            "param_name": "filter.v.option.color",
            "inactive_values": [],
            "operator": "OR"
          },
          {
            "presentation": "text",
            "url_to_remove": "",
            "values": [
              { "count": 19, "active": false, "label": "ABITO", "value": "ABITO", "param_name": "filter.p.product_type" }
            ],
            "label": "Product Type",
            "active_values": [],
            "type": "list",
            "param_name": "filter.p.product_type",
            "inactive_values": [],
            "operator": "OR"
          },
          {
            "url_to_remove": "",
            "values": [
              { "count": 14, "active": false, "label": "In Stock", "value": "1", "param_name": "filter.v.availability" },
              { "count": 5, "active": false, "label": "Out of Stock", "value": "0", "param_name": "filter.v.availability" }
            ],
            "label": "Availability",
            "active_values": [],
            "type": "boolean",
            "param_name": "filter.v.availability",
            "inactive_values": []
          }
        ],
        "performed": true,
        "resultsCount": 4,
        "sortBy": "relevance",
        "sortOptions": [
          "relevance",
          "price-ascending",
          "price-descending"
        ],
        "terms": query,
        "types": ["product"],
        "results": [
          {
            "available": true,
            "category": {},
            "collections": [],
            "compareAtPrice": null,
            "compareAtPriceMax": null,
            "compareAtPriceMin": null,
            "compareAtPriceVaries": false,
            "content": "<p>Abito bretelline con borchiette allo scollo. Composizione: 95% Viscosa 5% Elastan. <br>La modella è alta 170cm e indossa una S.<br>Colore: nero<br>REF: VENICER</p>",
            "createdAt": "2025-10-03T13:30:08+02:00",
            "description": "<p>Abito bretelline con borchiette allo scollo. Composizione: 95% Viscosa 5% Elastan. <br>La modella è alta 170cm e indossa una S.<br>Colore: nero<br>REF: VENICER</p>",
            "featuredImage": {
              "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/vestitonero1.jpg?v=1759747215",
              "alt": "",
              "id": 83588466540889
            },
            "featuredMedia": {},
            "firstAvailableVariant": {
              "inventory_quantity": 2,
              "price": "39.90",
              "available": true,
              "id": 56287071895897,
              "title": "XS / BLACK"
            },
            "giftCard": false,
            "handle": "donna-abito-con-borchie-venicer-black-a012953",
            "hasOnlyDefaultVariant": false,
            "id": 15844790731097,
            "images": [
              {
                "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/vestitonero1.jpg?v=1759747215",
                "alt": "",
                "id": 83588466540889
              },
              {
                "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/Acquisizione-senza-titolo51266.jpg?v=1761841443",
                "alt": "",
                "id": 85096611316057
              },
              {
                "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/Acquisizione-senza-titolo51268.jpg?v=1761841443",
                "alt": "",
                "id": 85096611348825
              },
              {
                "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/Acquisizione-senza-titolo51271.jpg?v=1761841442",
                "alt": "",
                "id": 85096611381593
              }
            ],
            "media": [],
            "metafields": {},
            "options": ["Size", "Color"],
            "optionsByName": {},
            "optionsWithValues": [],
            "price": "39.90",
            "priceMax": "39.90",
            "priceMin": "39.90",
            "priceVaries": false,
            "publishedAt": "2025-10-06T12:40:55+02:00",
            "quantityPriceBreaksConfigured": false,
            "requiresSellingPlan": false,
            "selectedOrFirstAvailableSellingPlanAllocation": {},
            "selectedOrFirstAvailableVariant": {
              "inventory_quantity": 2,
              "price": "39.90",
              "available": true,
              "id": 56287071895897,
              "title": "XS / BLACK"
            },
            "selectedSellingPlan": null,
            "selectedSellingPlanAllocation": null,
            "selectedVariant": null,
            "sellingPlanGroups": [],
            "tags": ["AI25", "PARTY CAPSULE", "var-VENICER", "VESTITI"],
            "templateSuffix": "",
            "title": "Abito con borchie",
            "type": {
              "value": "ABITO"
            },
            "url": {},
            "variants": [
              {
                "inventory_quantity": 2,
                "compare_at_price": null,
                "price": "39.90",
                "available": true,
                "id": 56287071895897,
                "title": "XS / BLACK"
              },
              {
                "inventory_quantity": 2,
                "compare_at_price": null,
                "price": "39.90",
                "available": true,
                "id": 56287071928665,
                "title": "S / BLACK"
              },
              {
                "inventory_quantity": 2,
                "compare_at_price": null,
                "price": "39.90",
                "available": true,
                "id": 56287071961433,
                "title": "M / BLACK"
              }
            ],
            "variantsCount": 3,
            "vendor": "Suite Benedict"
          },
          {
            "available": true,
            "category": {},
            "collections": [],
            "compareAtPrice": null,
            "compareAtPriceMax": null,
            "compareAtPriceMin": null,
            "compareAtPriceVaries": false,
            "content": "<p>Abito con gonna a balze. Made in Italy. Composizione: 90% Cotone 10% Elastane. <br>La modella è alta 170cm e indossa una XS.<br>Colore: blu<br>REF: VURBETTO</p>",
            "createdAt": "2025-07-29T15:30:29+02:00",
            "description": "<p>Abito con gonna a balze. Made in Italy. Composizione: 90% Cotone 10% Elastane. <br>La modella è alta 170cm e indossa una XS.<br>Colore: blu<br>REF: VURBETTO</p>",
            "featuredImage": {
              "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/Acquisizione-senza-titolo22335.jpg?v=1754583000",
              "alt": "",
              "id": 80474919010649
            },
            "featuredMedia": {},
            "firstAvailableVariant": {
              "inventory_quantity": 4,
              "price": "39.90",
              "available": true,
              "id": 55813882904921,
              "title": "M / BLU NAVY"
            },
            "giftCard": false,
            "handle": "donna-abito-con-gonna-a-balze-vurbetto-blu-navy-a012822",
            "hasOnlyDefaultVariant": false,
            "id": 15653527421273,
            "images": [
              {
                "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/Acquisizione-senza-titolo22335.jpg?v=1754583000",
                "alt": "",
                "id": 80474919010649
              },
              {
                "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/Acquisizione-senza-titolo22337.jpg?v=1754583000",
                "alt": "",
                "id": 80474919043417
              },
              {
                "src": "https://cdn.shopify.com/s/files/1/0651/5336/8301/files/Acquisizione-senza-titolo22339.jpg?v=1754583001",
                "alt": "",
                "id": 80474919076185
              }
            ],
            "media": [],
            "metafields": {},
            "options": ["Size", "Color"],
            "optionsByName": {},
            "optionsWithValues": [],
            "price": "39.90",
            "priceMax": "39.90",
            "priceMin": "39.90",
            "priceVaries": false,
            "publishedAt": "2025-08-25T10:35:07+02:00",
            "quantityPriceBreaksConfigured": false,
            "requiresSellingPlan": false,
            "selectedOrFirstAvailableSellingPlanAllocation": {},
            "selectedOrFirstAvailableVariant": {
              "inventory_quantity": 4,
              "price": "39.90",
              "available": true,
              "id": 55813882904921,
              "title": "M / BLU NAVY"
            },
            "selectedSellingPlan": null,
            "selectedSellingPlanAllocation": null,
            "selectedVariant": null,
            "sellingPlanGroups": [],
            "tags": ["AI25", "PARTY CAPSULE", "var-VURBETTO", "VESTITI"],
            "templateSuffix": "",
            "title": "Abito con gonna a balze",
            "type": {
              "value": "ABITO"
            },
            "url": {},
            "variants": [
              {
                "inventory_quantity": 0,
                "compare_at_price": null,
                "price": "39.90",
                "available": false,
                "id": 55813882839385,
                "title": "XS / BLU NAVY"
              },
              {
                "inventory_quantity": 0,
                "compare_at_price": null,
                "price": "39.90",
                "available": false,
                "id": 55813882872153,
                "title": "S / BLU NAVY"
              },
              {
                "inventory_quantity": 4,
                "compare_at_price": null,
                "price": "39.90",
                "available": true,
                "id": 55813882904921,
                "title": "M / BLU NAVY"
              },
              {
                "inventory_quantity": 0,
                "compare_at_price": null,
                "price": "39.90",
                "available": false,
                "id": 55813882937689,
                "title": "L / BLU NAVY"
              }
            ],
            "variantsCount": 4,
            "vendor": "Suite Benedict"
          }
        ]
      });
    },

    /**
     * Show API results (Shopify results are already hidden by default in template)
     */
    showBlankPage: function (apiResults) {
      console.log('[CustomSearch] Showing API results:', apiResults);

      // IMPORTANT: Completely remove Shopify results from DOM to prevent conflicts
      const shopifyResults = document.getElementById('shopifySearchResults');
      if (shopifyResults) {
        console.log('[CustomSearch] Removing Shopify results from DOM');
        shopifyResults.remove();
      }

      // Also remove any Shopify collection content that might conflict
      const shopifyCollection = document.getElementById('CollectionAjaxContent');
      if (shopifyCollection && !shopifyCollection.closest('#customSearchResults')) {
        console.log('[CustomSearch] Removing Shopify collection content');
        shopifyCollection.remove();
      }

      // Create custom results container
      const searchContent = document.querySelector('.search-content .page-width');
      if (searchContent) {
        // Create container for custom results
        let customContainer = document.getElementById('customSearchResults');
        if (!customContainer) {
          customContainer = document.createElement('div');
          customContainer.id = 'customSearchResults';
          searchContent.appendChild(customContainer);
        }

        // Build HTML matching Shopify's EXACT structure
        let html = `
          <!-- HR separator (from Shopify default) -->
          <hr class="hr--medium">

          <!-- Section header for no results message -->
          <div class="section-header">
        `;

        // Add no results message if no products found
        if (!apiResults.results || apiResults.results.length === 0) {
          const searchTerms = apiResults.terms || '';
          html += `
            <p>
              Your search for "${this.escapeHtml(searchTerms)}" did not yield any results.
            </p>
          `;
        }

        html += `
          </div>

          <div id="CollectionAjaxContent" class="grid grid--uniform" data-scroll-to>

            <!-- Sidebar with filters (1/5 width) -->
            <div class="grid__item medium-up--one-fifth grid__item--sidebar">
              ${this.renderActiveFilterTags(apiResults)}
              ${this.renderFilters(apiResults)}
            </div>

            <!-- Content area with products (4/5 width) -->
            <div class="grid__item medium-up--four-fifths grid__item--content">
              <div class="collection-grid__wrapper">

                <!-- Filter bar with count and sort -->
                <div class="collection-filter">
                  <div class="collection-filter__item collection-filter__item--drawer">
                    <button type="button" class="js-drawer-open-collection-filters btn btn--tertiary" aria-controls="FilterDrawer" aria-expanded="false">
                      <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-filter" viewBox="0 0 64 64"><title>icon-filter</title><path d="M48 42h10m-10 0a5 5 0 1 1-5-5 5 5 0 0 1 5 5ZM7 42h31M16 22H6m10 0a5 5 0 1 1 5 5 5 5 0 0 1-5-5Zm41 0H26"></path></svg>
                      Filter
                    </button>
                  </div>
                  <div class="collection-filter__item collection-filter__item--count small--hide">
                    ${apiResults.resultsCount} ${apiResults.resultsCount === 1 ? 'result' : 'results'}
                  </div>
                  ${this.renderSortingDropdown(apiResults)}
                </div>

                <!-- Product grid -->
        `;

        if (apiResults.results && apiResults.results.length > 0) {
          html += '<div class="grid grid--uniform">';

          apiResults.results.forEach((product) => {
            html += this.renderProductCard(product);
          });

          html += '</div>';

          // Add Shopify Quick Shop Modal containers for desktop
          html += '<!-- Quick Shop Modals for Desktop -->';
          apiResults.results.forEach((product) => {
            html += this.renderQuickShopModalContainer(product);
          });
        } else {
          html += '<p style="padding: 40px; text-align: center; color: #999;">No products found</p>';
        }

        html += `
              </div>
            </div>

          </div>
        `;

        customContainer.innerHTML = html;
        customContainer.style.display = 'block';

        // Add Shopify-style CSS for proper desktop layout
        this.addShopifyStyleCSS();

        // Initialize event handlers
        this.initQuickAddHandlers();
        this.initSortingHandler();
        this.initFilterHandlers();
        this.initFilterTagHandlers();
        this.initCollapsibleFilters();
        this.initMobileFilterDrawer();

        // Update filter button state on initial render
        this.updateFilterButtonState();

        // Initialize desktop quick shop after DOM is ready and Shopify theme.js is loaded
        // Increased delay to ensure theme.js modals system is fully initialized
        setTimeout(() => {
          this.initDesktopQuickShop();
        }, 500);
      }
    },

    /**
     * Add Shopify-style CSS for proper desktop layout
     */
    addShopifyStyleCSS: function () {
      // Check if style already exists
      if (document.getElementById('custom-search-shopify-style')) return;

      const style = document.createElement('style');
      style.id = 'custom-search-shopify-style';
      style.textContent = `
        /* Hide filter button on desktop when sidebar is visible */
        @media screen and (min-width: 769px) {
          #customSearchResults .collection-filter__item--drawer {
            display: none;
          }
        }
      `;
      document.head.appendChild(style);
    },

    /**
     * Render complete product card with all features
     */
    renderProductCard: function (product) {
      const productUrl = Shopify.routes.root + `products/${product.handle}`;
      // Handle both compareAtPrice and compare_at_price formats, parse to numbers
      const price = this.parsePrice(product.price);
      const compareAtPrice = this.parsePrice(product.compareAtPrice || product.compare_at_price);
      const onSale = compareAtPrice && price && compareAtPrice > price;
      const images = product.images || [];

      // Check if product is new (published within last 10 days)
      const isNew = this.isProductNew(product.publishedAt);

      let html = `
        <div class="grid__item grid-product small--one-half medium-up--one-quarter grid-product__has-quick-shop aos-init aos-animate"
             data-aos="row-of-4"
             data-product-handle="${product.handle}"
             data-product-id="${product.id}">
          <div class="grid-product__content">
      `;

      // Badges (Sale, New, Notify me)
      if (!product.available) {
        html += `<div class="grid-product__tag grid-product__tag--sold-out">Notify me</div>`;
      } else if (onSale) {
        const discount = Math.round(((compareAtPrice - price) / compareAtPrice) * 100);
        html += `<div class="grid-product__tag grid-product__tag--sale" style="background: black; color: white;">${discount}% off</div>`;
      } else if (isNew) {
        html += `<div class="grid-product__tag mmbadge-new"><span class="new-badge">NEW!</span></div>`;
      }

      // Image wrapper with carousel
      html += `
        <div class="grid__item-image-wrapper">
          <div class="justAwrapperCard">
            <a href="${productUrl}">
              <div class="grid-product__image-mask">
                <div class="image-wrap loaded" style="height: 0; padding-bottom: 129.87012987012986%;">
      `;

      // Use swiper carousel if multiple images exist
      if (images.length > 1) {
        // Only enable loop if there are enough images (4+)
        const enableLoop = images.length >= 4;

        html += `
                  <swiper-container class="custom-image-swiper" navigation="true" loop="${enableLoop}" allow-touch-move="false">
        `;

        images.forEach((image) => {
          const imgSrc = image.src || image;
          const imgSrcWithWidth = imgSrc.includes('?') ? `${imgSrc}&width=1080` : `${imgSrc}?width=1080`;

          html += `
                    <swiper-slide>
                      <img src="${imgSrcWithWidth}"
                           alt="${this.escapeHtml(product.title)}"
                           srcset="${this.generateSrcset(imgSrc)}"
                           width="1080"
                           loading="lazy"
                           class="grid-product__image"
                           sizes="(min-width: 769px) 25vw, 50vw"
                           style="width: 100%; height: auto; display: block; pointer-events: none;">
                    </swiper-slide>
          `;
        });

        html += `
                  </swiper-container>
        `;
      } else {
        // Single image (no carousel)
        const imageUrl = product.featuredImage && product.featuredImage.src
          ? product.featuredImage.src
          : (images.length > 0 ? images[0].src : '');

        if (imageUrl) {
          const imgSrcWithWidth = imageUrl.includes('?') ? `${imageUrl}&width=1080` : `${imageUrl}?width=1080`;
          html += `
                  <img src="${imgSrcWithWidth}"
                       alt="${this.escapeHtml(product.title)}"
                       srcset="${this.generateSrcset(imageUrl)}"
                       width="1080"
                       loading="lazy"
                       class="grid-product__image"
                       sizes="(min-width: 769px) 25vw, 50vw"
                       style="width: 100%; height: auto; display: block; pointer-events: none;">
          `;
        } else {
          html += `<div style="width: 100%; padding-bottom: 100%; background: #f5f5f5; pointer-events: none;"></div>`;
        }
      }

      html += `
                </div>
                <button class="quick-product__btn quick-product__btn--not-ready js-modal-open-quick-modal-${product.id} small--hide" aria-expanded="false">
                  ADD TO CART
                </button>
              </div>
            </a>
      `;

      // Quick Add button (mobile) and modal - inside justAwrapperCard
      html += this.renderQuickAddModal(product);

      html += `
          </div>
      `;

      // Product link with title and price - still inside grid__item-image-wrapper
      html += `
          <a href="${productUrl}" class="grid-product__link">
            <div class="grid-product__meta">
              <div class="grid-product__title grid-product__title--body">
                ${this.escapeHtml(product.title)}
              </div>
              <div class="grid-product__price">
                ${this.renderPriceThemed(product)}
              </div>
            </div>
          </a>
        </div>
      `;

      // Variant swatches (colors) - after grid__item-image-wrapper but inside grid-product__content
      html += this.renderVariantSwatches(product);

      html += `
          </div>
        </div>
      `;

      return html;
    },

    /**
     * Check if product is new (within last 10 days)
     */
    isProductNew: function (publishedAt) {
      if (!publishedAt) return false;
      const publishDate = new Date(publishedAt);
      const now = new Date();
      const diffTime = Math.abs(now - publishDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 10;
    },

    /**
     * Render sorting dropdown
     */
    renderSortingDropdown: function (apiResults) {
      // Label mapping for sort options
      const sortLabels = {
        'relevance': 'Relevance',
        'price-ascending': 'Price: Low to High',
        'price-descending': 'Price: High to Low',
        'title-ascending': 'A-Z',
        'title-descending': 'Z-A',
        'created-descending': 'Date: New to Old',
        'created-ascending': 'Date: Old to New',
        'best-selling': 'Best Selling'
      };

      // Use API sortOptions if available, otherwise use defaults
      const apiSortOptions = apiResults.sortOptions || [];
      const sortOptions = apiSortOptions.length > 0
        ? apiSortOptions.map(value => ({
          value: value,
          label: sortLabels[value] || value.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        }))
        : [
          { value: 'relevance', label: 'Relevance' },
          { value: 'price-ascending', label: 'Price: Low to High' },
          { value: 'price-descending', label: 'Price: High to Low' },
          { value: 'title-ascending', label: 'A-Z' },
          { value: 'title-descending', label: 'Z-A' },
          { value: 'created-descending', label: 'Date: New to Old' },
          { value: 'created-ascending', label: 'Date: Old to New' },
          { value: 'best-selling', label: 'Best Selling' }
        ];

      const currentSort = apiResults.sortBy || apiResults.defaultSortBy || 'relevance';

      let html = `
        <div class="collection-filter__item collection-filter__item--sort">
          <div class="collection-filter__sort-container">
            <label for="SortBy" class="hidden-label">Sort</label>
            <select name="SortBy" id="SortBy" data-default-sortby="${currentSort}" data-custom-search-sort>
              <option value="title-ascending">Sort</option>
      `;

      sortOptions.forEach(option => {
        const selected = option.value === currentSort ? 'selected="selected"' : '';
        html += `<option value="${option.value}" ${selected}>${option.label}</option>`;
      });

      html += `
            </select>
          </div>
        </div>
      `;

      return html;
    },

    /**
     * Render active filter tags (above filter sidebar)
     */
    renderActiveFilterTags: function (apiResults) {
      if (!apiResults.filters) return '';

      const activeTags = [];

      // Collect all active filter values by checking the 'active' property
      apiResults.filters.forEach(filter => {
        if (filter.values) {
          filter.values.forEach(filterValue => {
            // Check if this filter value is active (checked)
            if (filterValue.active === true) {
              activeTags.push({
                label: filterValue.label,
                value: filterValue.value,
                paramName: filter.param_name,
                filterLabel: filter.label
              });
            }
          });
        }
      });

      if (activeTags.length === 0) return '';

      let html = `
        <ul class="no-bullets tag-list tag-list--active-tags">
      `;

      activeTags.forEach(tag => {
        // Build removal URL (search without this filter)
        const urlParams = new URLSearchParams(window.location.search);
        const query = urlParams.get('q') || '';
        const removalUrl = `${Shopify.routes.root}search?q=${encodeURIComponent(query)}`;

        html += `
          <li class="tag tag--remove">
            <a class="btn btn--small"
               href="${removalUrl}"
               data-remove-filter="${tag.paramName}"
               data-remove-filter-value="${this.escapeHtml(tag.value)}">
              ${this.escapeHtml(tag.label)}
            </a>
            <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64">
              <title>icon-X</title>
              <path d="m19 17.61 27.12 27.13m0-27.12L19 44.74"/>
            </svg>
          </li>
        `;
      });

      html += `
        </ul>
      `;

      return html;
    },

    /**
     * Render filters sidebar (includes both desktop sidebar and mobile drawer)
     */
    renderFilters: function (apiResults) {
      if (!apiResults.filters || !Array.isArray(apiResults.filters) || apiResults.filters.length === 0) {
        return '';
      }

      let html = `
        <div id="CollectionSidebar" data-style="sidebar">
          <!-- Desktop Sidebar (hidden on mobile) -->
          <div class="collection-sidebar small--hide">
            <form class="filter-form">
      `;

      // Render filter groups for desktop sidebar
      apiResults.filters.forEach((filter, filterIndex) => {
        if (!filter.values || filter.values.length === 0) return;
        html += this.renderFilterGroup(filter, filterIndex + 1);
      });

      html += `
            </form>
          </div>

          <!-- Mobile Filter Drawer -->
          <div id="FilterDrawer" class="drawer drawer--left">
            <div class="drawer__contents">
              <div class="drawer__fixed-header">
                <div class="drawer__header appear-animation appear-delay-1">
                  <div class="h2 drawer__title">
                    Filter
                  </div>
                  <div class="drawer__close">
                    <button type="button" class="drawer__close-button js-drawer-close">
                      <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64">
                        <title>icon-X</title>
                        <path d="m19 17.61 27.12 27.13m0-27.12L19 44.74"/>
                      </svg>
                      <span class="icon__fallback-text">Close menu</span>
                    </button>
                  </div>
                </div>
              </div>
              <div class="drawer__scrollable appear-animation appear-delay-2">
                <form class="filter-form">
      `;

      // Render filter groups for mobile drawer
      apiResults.filters.forEach((filter, filterIndex) => {
        if (!filter.values || filter.values.length === 0) return;
        html += this.renderFilterGroup(filter, filterIndex + 1, true); // true = mobile drawer
      });

      html += `
                </form>
              </div>
            </div>
          </div>
        </div>
      `;

      return html;
    },

    /**
     * Helper to capitalize filter keys (e.g., "productTypes" -> "Product Types")
     */
    capitalizeFilterKey: function (key) {
      // Convert camelCase to space-separated words
      const words = key.replace(/([A-Z])/g, ' $1').trim();
      // Capitalize first letter of each word
      return words.split(' ').map(word =>
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ');
    },

    /**
     * Render a single filter group with collapsible structure
     */
    renderFilterGroup: function (filter, filterIndex, isMobileDrawer = false) {
      const label = filter.label || 'Filter';
      const filterId = `filter-${filter.param_name.replace(/\./g, '-')}`;
      const location = isMobileDrawer ? 'SidebarDrawer' : 'CollectionSidebar';
      const groupId = `${location}-${filterIndex}-${filterId}`;

      // Filters should be EXPANDED by default on desktop (matching Shopify behavior)
      const collapsed = false; // Shopify's default is to show filters expanded

      let html = `
        <div class="collection-sidebar__group--${filterIndex}">
          <div class="collection-sidebar__group">
            <button type="button"
                    class="collapsible-trigger collapsible-trigger-btn collapsible--auto-height${!collapsed ? ' is-open' : ''} tag-list__header"
                    aria-controls="${groupId}"
                    data-collapsible-id="${filterId}"
                    aria-expanded="${!collapsed}">
              ${this.escapeHtml(label)}
              <span class="collapsible-trigger__icon collapsible-trigger__icon--open">
                <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-chevron-down" viewBox="0 0 284.49 284.49">
                  <path d="M284.49,67.5A14.23,14.23,0,0,0,274.3,57.31L153.74,177.87a14.23,14.23,0,0,1-20.19,0L12.99,57.31A14.24,14.24,0,1,0-7.2,77.5L113.36,198.06a42.71,42.71,0,0,0,60.57,0L294.49,77.5A14.23,14.23,0,0,0,284.49,67.5Z" transform="translate(-7.25 -52.25)"/>
                </svg>
              </span>
            </button>
            <div id="${groupId}"
                 class="collapsible-content collapsible-content--sidebar${!collapsed ? ' is-open' : ''}"
                 data-collapsible-id="${filterId}"
                 style="${!collapsed ? 'height: auto;' : 'height: 0;'}">
              <div class="collapsible-content__inner">
                <ul class="no-bullets tag-list">
      `;

      // Render filter values
      filter.values.forEach((filterValue, valueIndex) => {
        const inputId = isMobileDrawer
          ? `drawer-tag-${filterValue.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}-${valueIndex}`
          : `tag-${filterValue.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '')}`;
        const sanitizedValue = this.escapeHtml(filterValue.value);
        const sanitizedLabel = this.escapeHtml(filterValue.label);

        html += `
                  <li class="tag${filterValue.active ? ' tag--active' : ''}">
                    <label id="${inputId}" class="tag__checkbox-wrapper text-label">
                      <input for="${inputId}"
                             type="checkbox"
                             class="tag__input"
                             name="${filterValue.param_name}"
                             value="${sanitizedValue}"
                             data-custom-search-filter
                             data-filter-param="${filter.param_name}"
                             ${filterValue.active ? 'checked' : ''}>
                      <span class="tag__checkbox"></span>
                      <span>
                        <span class="tag__text">${sanitizedLabel}</span> (${filterValue.count})
                      </span>
                    </label>
                  </li>
        `;
      });

      html += `
                </ul>
              </div>
            </div>
          </div>
        </div>
      `;

      return html;
    },

    /**
     * Render variant color swatches
     */
    renderVariantSwatches: function (product) {
      if (!product.variants || product.variants.length <= 1) return '';

      // Extract unique colors from variants
      const colors = new Set();
      product.variants.forEach(variant => {
        // Assuming color is in option2 (adjust based on your product structure)
        if (variant.title && variant.title.includes('/')) {
          const color = variant.title.split('/')[1]?.trim();
          if (color) colors.add(color);
        }
      });

      if (colors.size <= 1) return '';

      const productUrl = Shopify.routes.root + `products/${product.handle}`;
      let html = `<div class="grid-product__colors grid-product__colors--${product.id}">`;
      colors.forEach(color => {
        // Convert color name to CSS color value
        const colorCSS = this.getColorCSS(color);
        const colorSlug = color.toLowerCase().replace(/\s+/g, '-');

        html += `
          <a href="${productUrl}" class="color-swatch color-swatch--small color-swatch--${colorSlug}"
             style="background-color: ${colorCSS};"
             title="${color}">
          </a>
        `;
      });
      html += '</div>';

      return html;
    },

    /**
     * Parse price - handles both string ("39.90") and number (39.90) formats
     */
    parsePrice: function (price) {
      if (price === null || price === undefined) {
        return null;
      }

      // If it's already a number, return it
      if (typeof price === 'number') {
        return price;
      }

      // If it's a string, parse it
      if (typeof price === 'string') {
        const parsed = parseFloat(price);
        return isNaN(parsed) ? null : parsed;
      }

      return null;
    },

    /**
     * Get CSS color value from color name
     */
    getColorCSS: function (colorName) {
      const colorMap = {
        // Italian color names (for Suite Benedict)
        'NERO': '#000000',
        'BIANCO': '#FFFFFF',
        'ROSSO': '#FF0000',
        'BLU': '#0000FF',
        'VERDE': '#008000',
        'GIALLO': '#FFFF00',
        'ROSA': '#FFC0CB',
        'MARRONE': '#8B4513',
        'GRIGIO': '#808080',
        'ARANCIONE': '#FFA500',
        'VIOLA': '#800080',
        'BEIGE': '#F5F5DC',
        'NAVY': '#000080',
        'TURCHESE': '#40E0D0',

        // English color names
        'BLACK': '#000000',
        'WHITE': '#FFFFFF',
        'RED': '#FF0000',
        'BLUE': '#0000FF',
        'GREEN': '#008000',
        'YELLOW': '#FFFF00',
        'PINK': '#FFC0CB',
        'BROWN': '#8B4513',
        'GRAY': '#808080',
        'GREY': '#808080',
        'ORANGE': '#FFA500',
        'PURPLE': '#800080',
        'BEIGE': '#F5F5DC',
        'NAVY': '#000080',
        'TURQUOISE': '#40E0D0',
        'GOLD': '#FFD700',
        'SILVER': '#C0C0C0',
        'CREAM': '#FFFDD0',
        'TAN': '#D2B48C',
        'KHAKI': '#F0E68C',
        'OLIVE': '#808000',
        'MINT': '#98FF98',
        'CORAL': '#FF7F50',
        'PEACH': '#FFDAB9',
        'LAVENDER': '#E6E6FA',
        'BURGUNDY': '#800020',
        'TEAL': '#008080',
        'IVORY': '#FFFFF0'
      };

      // Normalize color name (uppercase, trim)
      const normalized = colorName.toUpperCase().trim();

      // Return mapped color or fallback to the color name itself (CSS named colors)
      return colorMap[normalized] || colorName.toLowerCase();
    },

    /**
     * Render size selector
     */
    renderSizeSelector: function (product) {
      if (!product.variants || product.variants.length <= 1) return '';

      let html = `
        <div class="grid-product__sizes" style="margin-top: 10px;">
          <ul style="list-style: none; padding: 0; display: flex; gap: 5px; flex-wrap: wrap;">
      `;

      product.variants.forEach(variant => {
        const size = variant.title.split('/')[0]?.trim() || variant.title;
        const soldOut = !variant.available;

        html += `
          <li style="display: inline-block;" ${soldOut ? 'class="sold_out"' : ''}>
            <button class="size-selector-btn ${soldOut ? 'BIS_trigger' : ''}"
                    data-variant-id="${variant.id}"
                    style="padding: 5px 10px; border: 1px solid #ddd; background: ${soldOut ? '#f5f5f5' : 'white'};
                           cursor: ${soldOut ? 'not-allowed' : 'pointer'}; font-size: 12px;"
                    ${soldOut ? 'disabled' : ''}>
              ${size}
            </button>
          </li>
        `;
      });

      html += `
          </ul>
        </div>
      `;

      return html;
    },

    /**
     * Render Quick Add button and modal (Matches Shopify WeDev structure exactly)
     */
    renderQuickAddModal: function (product) {
      if (!product.variants || product.variants.length === 0) return '';

      let html = `
        <!-- Quick Add Button -->
        <div class="custom-quick-product__btn--${product.id}">
          <span class="quick-add-mobile">
            <svg class="Icon Icon--cart" width="32" height="32" viewBox="0 0 32 32">
              <path d="M0 16C0 7.163 7.163 0 16 0s16 7.163 16 16-7.163 16-16 16S0 24.837 0 16z"></path>
              <path clip-rule="evenodd" d="M21.704 23.605h-11.27a2.132 2.132 0 01-1.574-.668c-.4-.428-.586-1-.512-1.57l1.113-8.387h13.217l1.1 8.387c.143 1.091-.666 2.087-1.81 2.225l-.264.013zM18.852 10.988c0-1.467-1.245-2.656-2.782-2.656s-2.783 1.19-2.783 2.656v1.992h5.565v-1.992z" stroke="#000" stroke-linecap="round" stroke-linejoin="round"></path>
              <g>
                <path d="M18.571 17.845h-2.035a.107.107 0 01-.107-.107v-2.036a.428.428 0 10-.858 0v2.036a.107.107 0 01-.107.107H13.43a.429.429 0 100 .857h2.035a.107.107 0 01.107.107v2.036a.429.429 0 00.858 0v-2.036a.107.107 0 01.107-.107h2.035a.429.429 0 100-.857z" fill="#000"></path>
              </g>
            </svg>
          </span>
        </div>

        <!-- WeDev (Quick-add)-->
        <div class="custom-quick-add-modal custom-quick-add-modal--${product.id}">
          <button class="custom-close-btn">
            <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64"><path d="M19 17.61l27.12 27.13m0-27.12L19 44.74"/></svg>
          </button>
          <div class="modal_title">Select size</div>
          <ul>
      `;

      product.variants.forEach((variant, index) => {
        const size = variant.title.split('/')[0]?.trim() || variant.title;
        const soldOut = !variant.available;
        const isPreorder = false; // Add preorder logic if needed
        const productDataEscaped = this.escapeHtml(JSON.stringify(product));

        html += `
            <li ${soldOut ? 'class="sold_out"' : ''}>
              <input type="radio"
                     id="${product.id}--${index + 1}"
                     data-is-preorder="${isPreorder}"
                     name="${product.id}"
                     data-variant-id="${variant.id}"
                     data-product-data="${productDataEscaped}"
                     ${soldOut ? 'class="BIS_trigger"' : ''}>
              <label for="${product.id}--${index + 1}">
                ${size}
              </label>
            </li>
        `;
      });

      html += `
          </ul>
          <button class="add_to_cart add-to-cart" disabled>
            <span> Add To Cart</span>
          </button>
        </div>
      `;

      return html;
    },

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml: function (text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    /**
     * Render Shopify Quick Shop Modal Container (Desktop)
     */
    renderQuickShopModalContainer: function (product) {
      return `
        <div id="QuickShopModal-${product.id}" class="modal modal--square modal--quick-shop" data-product-id="${product.id}">
          <div class="modal__inner">
            <div class="modal__centered">
              <div class="modal__centered-content">
                <div id="QuickShopHolder-${product.handle}"></div>
              </div>
              <button type="button" class="modal__close js-modal-close text-link">
                <svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" viewBox="0 0 64 64">
                  <title>icon-X</title>
                  <path d="m19 17.61 27.12 27.13m0-27.12L19 44.74"/>
                </svg>
                <span class="icon__fallback-text">Close</span>
              </button>
            </div>
          </div>
        </div>
      `;
    },

    /**
     * Generate responsive srcset for images
     */
    generateSrcset: function (imageSrc) {
      if (!imageSrc) return '';

      const widths = [360, 540, 720, 900, 1080];
      const srcsetParts = widths.map(width => {
        const src = imageSrc.includes('?')
          ? `${imageSrc}&width=${width}`
          : `${imageSrc}?width=${width}`;
        return `${src} ${width}w`;
      });

      return srcsetParts.join(', ');
    },

    /**
     * Format price with Shopify's money format (dynamic currency symbol)
     */
    formatPrice: function (amount, useWithCurrency = false) {
      if (amount === null || amount === undefined) return '';

      // Parse amount to number
      const numericAmount = parseFloat(amount);

      // Get format string from window.ShopifyMoneyFormat (defined in theme.liquid)
      const formatString = useWithCurrency
        ? (window.ShopifyMoneyFormat?.formatWithCurrency || '€{{amount}}')
        : (window.ShopifyMoneyFormat?.format || '€{{amount}}');

      // Format amounts with different separators
      const amountWithPeriod = numericAmount.toFixed(2); // e.g., "39.90"
      const amountNoDecimals = Math.round(numericAmount).toString(); // e.g., "40"

      // European format (German/Hungarian/etc): period for thousands, comma for decimals
      // e.g., "15.700,50" or "15.700" (no decimals)
      const amountWithCommaAndThousands = numericAmount.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });

      const roundedAmount = Math.round(numericAmount);
      const amountNoDecimalsWithPeriodThousands = roundedAmount.toLocaleString('de-DE'); // e.g., "15.700"

      // Replace all possible Shopify money format placeholders
      let formatted = formatString
        .replace('{{amount_with_comma_separator}}', amountWithCommaAndThousands)
        .replace('{{amount_no_decimals_with_comma_separator}}', amountNoDecimalsWithPeriodThousands)
        .replace('{{amount_no_decimals}}', amountNoDecimals)
        .replace('{{amount_with_apostrophe_separator}}', amountWithPeriod.replace('.', "'"))
        .replace('{{amount}}', amountWithPeriod)
        .trim();

      return formatted;
    },

    /**
     * Render price with theme styling
     */
    renderPriceThemed: function (product) {
      // Parse prices (handles both string and number formats)
      const price = this.parsePrice(product.price);
      const compareAtPrice = this.parsePrice(product.compareAtPrice || product.compare_at_price);
      const isOnSale = compareAtPrice && price && compareAtPrice > price;
      const formattedPrice = price ? this.formatPrice(price) : '';
      const formattedComparePrice = compareAtPrice ? this.formatPrice(compareAtPrice) : '';

      if (isOnSale) {
        // Match Shopify's format: show sale price prominently, then strikethrough original
        return `
          <span class="discountedPrice">${formattedPrice}</span>
          <span class="visually-hidden">Regular price</span>
          <span class="grid-product__price--original">${formattedComparePrice}</span>
        `;
      } else {
        return `${formattedPrice}`;
      }
    },

    /**
     * Render price with sale badge (legacy)
     */
    renderPrice: function (product) {
      // Parse prices (handles both string and number formats)
      const price = this.parsePrice(product.price);
      const compareAtPrice = this.parsePrice(product.compareAtPrice || product.compare_at_price);
      const isOnSale = compareAtPrice && price && compareAtPrice > price;

      // Format prices with dynamic currency
      const formattedPrice = price ? this.formatPrice(price) : '';
      const formattedComparePrice = compareAtPrice ? this.formatPrice(compareAtPrice) : '';

      if (isOnSale) {
        // On sale
        return `
          <span style="background: #ff4e4e; color: white; padding: 3px 8px; border-radius: 3px; font-size: 11px; font-weight: bold; text-transform: uppercase;">
            Sale
          </span>
          <span style="text-decoration: line-through; color: #999; font-size: 14px;">
            ${formattedComparePrice}
          </span>
          <span style="color: #ff4e4e; font-weight: bold; font-size: 16px;">
            ${formattedPrice}
          </span>
        `;
      } else {
        // Regular price
        return `<span style="font-size: 16px; font-weight: 500;">${formattedPrice}</span>`;
      }
    },

    /**
     * Initialize Desktop Quick Shop (uses Shopify's theme.js modal system)
     */
    initDesktopQuickShop: function () {
      console.log('[CustomSearch] Initializing desktop quick shop...');

      // Check if Shopify's theme functions are available
      if (typeof theme === 'undefined' || !theme.preloadProductModal) {
        console.warn('[CustomSearch] Shopify theme.js not available for desktop quick shop');
        return;
      }

      // Initialize quick shop for each product in custom search results
      const customSearchContainer = document.getElementById('customSearchResults');
      if (!customSearchContainer) {
        console.warn('[CustomSearch] Custom search results container not found');
        return;
      }

      // Find all products within custom search results
      const products = customSearchContainer.querySelectorAll('.grid-product');
      console.log('[CustomSearch] Found', products.length, 'products to initialize');

      products.forEach((el, index) => {
        const productId = el.dataset.productId;
        const handle = el.dataset.productHandle;

        // Look for button (it has --not-ready class initially)
        const btn = el.querySelector('.quick-product__btn');

        // Check if modal holder exists
        const holder = document.getElementById(`QuickShopHolder-${handle}`);

        if (!productId || !handle) {
          console.warn('[CustomSearch] Product', index, 'missing ID or handle:', el);
          return;
        }

        if (!btn) {
          console.warn('[CustomSearch] Quick shop button not found for product:', handle);
          return;
        }

        if (!holder) {
          console.error('[CustomSearch] QuickShopHolder not found for product:', handle, '- Modal will not load');
          return;
        }

        console.log('[CustomSearch] Preloading modal for product:', handle, 'ID:', productId);

        // Call Shopify's preloadProductModal - this will:
        // 1. Fetch /products/{handle}?view=modal
        // 2. Insert content into QuickShopHolder
        // 3. Create modal instance (stored as theme['quick-modal-{productId}'])
        // 4. Remove --not-ready class from button
        // 5. Bind click handlers automatically via theme.Modals
        theme.preloadProductModal(handle, productId, btn);

        //SearchAI desktop quick add to cart analytics
        attachDesktopQuickShopAnalytics(btn, handle, productId);
      });

      console.log('[CustomSearch] Desktop quick shop initialization complete');
    },

    /**
     * Initialize Quick Add modal handlers (jQuery-based like Shopify)
     */
    initQuickAddHandlers: function () {
      // Check if jQuery is available
      if (typeof jQuery === 'undefined') {
        console.warn('[CustomSearch] jQuery not available, using vanilla JS fallback');
        this.initQuickAddHandlersVanilla();
        return;
      }

      // Create shared modal overlay if not exists
      if ($('.custom-modal-overlay').length === 0) {
        $('body').append('<div class="custom-modal-overlay"></div>');
      }
      const $sharedOverlay = $('.custom-modal-overlay');

      // Use jQuery like Shopify's implementation
      const $quickAddButtons = $('.quick-add-mobile');

      $quickAddButtons.each(function (index) {
        const $quickAddBtn = $(this);
        // Find modal by looking in parent container
        const $container = $quickAddBtn.closest('[class*="custom-quick-product__btn"]');
        const productId = $container.attr('class').match(/custom-quick-product__btn--(\d+)/)[1];
        const $modal = $(`.custom-quick-add-modal--${productId}`);
        const $closeBtn = $modal.find('.custom-close-btn');
        const $addToCartBtn = $modal.find('.add_to_cart');
        const $quickAddInput = $modal.find('input[type="radio"]');
        const $quickAddLabel = $modal.find('label');

        // Handle Quick Add button click
        $quickAddBtn.on('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          $quickAddLabel.removeClass('selected-input');
          $modal.addClass('modal-is-open');
          $modal.css('display', 'block');
          $sharedOverlay.css('display', 'block');
        });

        // Handle close button
        $closeBtn.on('click', function (e) {
          e.preventDefault();
          $modal.css('display', 'none');
          $sharedOverlay.css('display', 'none');
          $modal.removeClass('modal-is-open');
          $quickAddLabel.removeClass('selected-input');
          $addToCartBtn.attr('disabled', true);
        });

        // Handle overlay click
        $sharedOverlay.on('click', function () {
          $('.custom-quick-add-modal').css('display', 'none');
          $sharedOverlay.css('display', 'none');
          $('.custom-quick-add-modal').removeClass('modal-is-open');
          $quickAddLabel.removeClass('selected-input');
          $('.add_to_cart').attr('disabled', true);
        });

        // Handle variant selection
        $quickAddInput.on('change', function () {
          if ($(this).is(':checked')) {
            const variantID = $(this).data('variant-id');
            const soldOut = $(this).parent('li').hasClass('sold_out');

            if (!soldOut) {
              $addToCartBtn.attr({
                'data-variant-id': variantID,
                'disabled': false
              });
            }
          }
        });

        // Handle label selection styling
        $quickAddInput.on('click', function () {
          $quickAddInput.each(function () {
            const $label = $(this).siblings('label');
            $label.removeClass('selected-input');
          });
          $(this).siblings('label').addClass('selected-input');
        });

        // Handle Add to Cart
        $addToCartBtn.on('click', function (e) {
          e.preventDefault();
          const variantID = $addToCartBtn.attr('data-variant-id');

          if (variantID) {
            // Track add to cart event with SearchAI Analytics
            if (window.SearchAI && window.SearchAI.Analytics) {
              const $checkedInput = $modal.find('input[type="radio"]:checked');
              const productIdFromInput = $checkedInput.attr('name');
              const $productCard = $(`.grid-product[data-product-id="${productId}"]`);
              const priceText = $productCard.find('.grid-product__price').text().trim() || '0';
              const currency = $('meta[property="og:price:currency"]').attr('content') ||
                $('meta[property="og:price:currency:code"]').attr('content') ||
                'EUR';

              const addToCartEvent = {
                type: "add_to_cart",
                product_id: productIdFromInput,
                variant_id: variantID,
                price: priceText,
                currency: currency,
                session_id: SearchAI.Session?.getSessionId?.(),
                client_id: SearchAI.Client?.getClientId?.(),
                timestamp: Date.now(),
                source: "search_quick_add"
              };

              if (!CustomSearch.isErrorFallback) {
                SearchAI.Analytics.report(addToCartEvent);
              }
            }

            const body = {
              items: [{
                id: variantID,
                quantity: 1
              }]
            };

            $.post('/cart/add.js', body, function () {
              $modal.css('display', 'none');
              $sharedOverlay.css('display', 'none');
              // Trigger Shopify cart events
              document.dispatchEvent(new CustomEvent('cart:build'));
              document.dispatchEvent(new CustomEvent('cart:open'));
            }).fail(function () {
              console.error('Failed to add to cart. Please try again.');
            });
          }
        });
      });
    },

    /**
     * Vanilla JS fallback for Quick Add handlers
     */
    initQuickAddHandlersVanilla: function () {
      const self = this;

      document.querySelectorAll('.quick-add-btn').forEach(btn => {
        const productId = btn.dataset.productId;
        const modal = document.querySelector(`.custom-quick-add-modal--${productId}`);
        const overlay = document.querySelector(`.custom-modal-overlay--${productId}`);
        const closeBtn = modal.querySelector('.custom-close-btn');
        const addToCartBtn = modal.querySelector('.add_to_cart');
        const inputs = modal.querySelectorAll('input[type="radio"]');

        // Open modal
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          modal.classList.add('modal-is-open');
          modal.style.display = 'block';
          overlay.style.display = 'block';
        });

        // Close modal
        const closeModal = () => {
          modal.style.display = 'none';
          overlay.style.display = 'none';
          modal.classList.remove('modal-is-open');
          addToCartBtn.disabled = true;
          addToCartBtn.style.opacity = '0.4';
        };

        closeBtn.addEventListener('click', closeModal);
        overlay.addEventListener('click', closeModal);

        // Handle variant selection
        inputs.forEach(input => {
          input.addEventListener('change', () => {
            if (input.checked && !input.closest('li').classList.contains('sold_out')) {
              addToCartBtn.setAttribute('data-variant-id', input.dataset.variantId);
              addToCartBtn.disabled = false;
              addToCartBtn.style.opacity = '1';
            }

            // Update label styling
            inputs.forEach(i => i.nextElementSibling.classList.remove('selected-input'));
            input.nextElementSibling.classList.add('selected-input');
          });
        });

        // Add to cart
        addToCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const variantId = addToCartBtn.getAttribute('data-variant-id');
          if (variantId) {
            self.addToCart(variantId, modal, overlay);
          }
        });
      });
    },

    /**
     * Add product to cart (vanilla JS version for fallback)
     */
    addToCart: function (variantId, modal, overlay) {
      // Track add to cart event with SearchAI Analytics
      if (window.SearchAI && window.SearchAI.Analytics && modal) {
        const checkedInput = modal.querySelector('input[type="radio"]:checked');
        const productId = checkedInput?.getAttribute('name');
        const modalClassMatch = modal.className.match(/custom-quick-add-modal--(\d+)/);
        const productIdFromModal = modalClassMatch ? modalClassMatch[1] : productId;
        const productCard = document.querySelector(`.grid-product[data-product-id="${productIdFromModal}"]`);
        const priceElement = productCard?.querySelector('.grid-product__price');
        const price = priceElement?.textContent?.trim() || '0';
        const currencyMeta = document.querySelector('meta[property="og:price:currency"]') ||
          document.querySelector('meta[property="og:price:currency:code"]');
        const currency = currencyMeta?.content || 'EUR';

        const addToCartEvent = {
          type: "add_to_cart",
          product_id: productIdFromModal,
          variant_id: variantId,
          price: price,
          currency: currency,
          session_id: SearchAI.Session?.getSessionId?.(),
          client_id: SearchAI.Client?.getClientId?.(),
          timestamp: Date.now(),
          source: "search_quick_add"
        };

        if (!CustomSearch.isErrorFallback) {
          SearchAI.Analytics.report(addToCartEvent);
        }
      }

      // Use Shopify's cart API
      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: 1
        })
      })
        .then(response => response.json())
        .then(data => {
          console.log('[CustomSearch] ✓ Added to cart:', data);

          // Close modal if provided
          if (modal && overlay) {
            modal.style.display = 'none';
            overlay.style.display = 'none';
          } else {
            // Close all modals as fallback
            document.querySelectorAll('.custom-quick-add-modal').forEach(m => {
              m.style.display = 'none';
            });
            document.querySelectorAll('.custom-modal-overlay').forEach(o => {
              o.style.display = 'none';
            });
          }

          // Trigger Shopify cart events
          document.dispatchEvent(new CustomEvent('cart:build'));
          document.dispatchEvent(new CustomEvent('cart:open'));

          // Fallback cart update
          if (window.theme && window.theme.cart) {
            window.theme.cart.getCart();
          }
        })
        .catch(error => {
          console.error('[CustomSearch] ✗ Add to cart failed:', error);
          // Silently fail like Shopify - no alert shown
        });
    },

    /**
     * Initialize sorting handler
     */
    initSortingHandler: function () {
      const sortSelect = document.querySelector('[data-custom-search-sort]');
      if (sortSelect) {
        sortSelect.addEventListener('change', (e) => {
          const sortBy = e.target.value;
          console.log('[CustomSearch] Sort changed to:', sortBy);

          // Get current search query
          const urlParams = new URLSearchParams(window.location.search);
          const query = urlParams.get('q');

          if (query) {
            // Re-execute search with new sort
            this.executeSearchWithFilters(query, sortBy, this.getActiveFilters());
          }
        });
      }
    },

    /**
     * Initialize filter handlers
     */
    initFilterHandlers: function () {
      const filterInputs = document.querySelectorAll('[data-custom-search-filter]');
      filterInputs.forEach(input => {
        input.addEventListener('change', () => {
          console.log('[CustomSearch] Filter changed');

          // Update filter button state immediately
          this.updateFilterButtonState();

          // Get current search query
          const urlParams = new URLSearchParams(window.location.search);
          const query = urlParams.get('q');

          if (query) {
            // Get current sort
            const sortSelect = document.querySelector('[data-custom-search-sort]');
            const sortBy = sortSelect ? sortSelect.value : 'relevance';

            // Re-execute search with filters
            this.executeSearchWithFilters(query, sortBy, this.getActiveFilters());
          }
        });
      });
    },

    /**
     * Initialize filter tag removal handlers
     */
    initFilterTagHandlers: function () {
      const tagLinks = document.querySelectorAll('[data-remove-filter]');

      tagLinks.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();

          const paramName = link.dataset.removeFilter;
          const value = link.dataset.removeFilterValue;

          console.log('[CustomSearch] Removing filter:', paramName, value);

          // Uncheck ALL matching checkboxes (desktop + mobile)
          let selectorValue = value;

          // If CSS.escape is available, use it to safely handle weird characters
          if (window.CSS && CSS.escape) {
            selectorValue = CSS.escape(selectorValue);
          } else {
            // minimal fallback for quotes
            selectorValue = selectorValue.replace(/"/g, '\\"');
          }

          const checkboxes = document.querySelectorAll(
            `[data-filter-param="${paramName}"][value="${selectorValue}"]`
          );

          checkboxes.forEach(cb => {
            cb.checked = false;
          });

          // Update filter button state immediately
          this.updateFilterButtonState();

          // Re-execute search without this filter
          const urlParams = new URLSearchParams(window.location.search);
          const query = urlParams.get('q');

          if (query) {
            const sortSelect = document.querySelector('[data-custom-search-sort]');
            const sortBy = sortSelect ? sortSelect.value : 'relevance';
            this.executeSearchWithFilters(query, sortBy, this.getActiveFilters());
          }
        });
      });
    },

    /**
     * Initialize collapsible filter groups
     */
    initCollapsibleFilters: function () {
      const triggers = document.querySelectorAll('.collapsible-trigger');

      triggers.forEach(trigger => {
        trigger.addEventListener('click', (e) => {
          e.preventDefault();

          const targetId = trigger.getAttribute('aria-controls');
          const content = document.getElementById(targetId);
          const isExpanded = trigger.getAttribute('aria-expanded') === 'true';

          if (content) {
            if (isExpanded) {
              // Collapse
              content.style.height = '0';
              content.style.overflow = 'hidden';
              content.classList.remove('is-open');
              trigger.setAttribute('aria-expanded', 'false');
              trigger.classList.remove('is-open');
            } else {
              // Expand
              content.style.height = 'auto';
              content.style.overflow = 'visible';
              content.classList.add('is-open');
              trigger.setAttribute('aria-expanded', 'true');
              trigger.classList.add('is-open');
            }
          }
        });
      });
    },

    /**
     * Get active filters from checkboxes
     */
    getActiveFilters: function () {
      const filters = {};

      document.querySelectorAll('[data-custom-search-filter]:checked').forEach(input => {
        const paramName = input.dataset.filterParam;
        const value = input.value;

        if (!filters[paramName]) {
          filters[paramName] = [];
        }
        filters[paramName].push(value);
      });

      return filters;
    },

    /**
     * Get count of active filters
     */
    getActiveFilterCount: function () {
      return document.querySelectorAll('[data-custom-search-filter]:checked').length;
    },

    /**
     * Update filter button state and active count display
     */
    updateFilterButtonState: function () {
      const filterCount = this.getActiveFilterCount();
      const filterBtn = document.querySelector('.js-drawer-open-collection-filters');

      if (!filterBtn) return;

      // Update button active state
      if (filterCount > 0) {
        filterBtn.classList.add('btn--tertiary-active');

        // Find all text nodes in the button
        const textNodes = Array.from(filterBtn.childNodes).filter(node =>
          node.nodeType === Node.TEXT_NODE
        );

        // Check if count already exists
        const hasCount = filterBtn.textContent.includes('(');

        if (!hasCount) {
          // Add count as a new text node after the "Filter" text
          const countNode = document.createTextNode(`\n(${filterCount})`);
          filterBtn.appendChild(countNode);
        } else {
          // Update existing count - find the text node with count and update it
          const countNode = textNodes.find(node => node.textContent.includes('('));
          if (countNode) {
            countNode.textContent = `\n(${filterCount})`;
          }
        }
      } else {
        filterBtn.classList.remove('btn--tertiary-active');

        // Remove count text node if it exists
        const textNodes = Array.from(filterBtn.childNodes).filter(node =>
          node.nodeType === Node.TEXT_NODE
        );

        const countNode = textNodes.find(node => node.textContent.includes('('));
        if (countNode) {
          filterBtn.removeChild(countNode);
        }
      }

      console.log('[CustomSearch] Updated filter button state:', { filterCount });
    },

    /**
     * Execute search with filters and sorting
     */
    executeSearchWithFilters: async function (query, sortBy, filters) {
      this.isErrorFallback = false; // Reset fallback status
      console.log('[CustomSearch] Executing filtered search:', { query, sortBy, filters });

      // Check A/B test group
      if (!this.shouldUseAI()) {
        this.fallbackToShopify();
        // Report analytics with Shopify product IDs
        const shopifyResults = this.getShopifyProductIds();
        reportSearchToAnalytics({ results: shopifyResults }, query, 0);
        return;
      }

      // Close mobile filter drawer if open (like Shopify does)
      if (typeof theme !== 'undefined' && theme.FilterDrawer && theme.FilterDrawer.isOpen) {
        theme.FilterDrawer.close();
      }

      try {
        const apiResults = await this.callCustomAPIWithFilters(query, sortBy, filters);

        if (apiResults && apiResults.performed && apiResults.results) {
          console.log('[CustomSearch] ✓ Filtered API Success! Got', apiResults.resultsCount, 'results');
          // -------------- Report Search To Analytics -----------------------
          reportSearchToAnalytics(apiResults, query, 1);
          // -----------------------------------------------------------------
          this.showBlankPage(apiResults);
        } else {
          throw new Error('Invalid API response');
        }
      } catch (error) {
        console.log('[CustomSearch] ✗ Filtered API Failed:', error.message);
        console.log('[CustomSearch] >>> Falling back to Shopify search');

        // Fallback to Shopify
        this.isErrorFallback = true;
        this.fallbackToShopify();

        // Skip analytics reporting for error-based fallback
        console.log('[CustomSearch] ℹ️ Error fallback marked, skipping analytics reporting.');
      }
    },

    /**
     * Call API with filters and sorting
     */
    callCustomAPIWithFilters: function (query, sortBy, filters) {
      // TEST MODE: Return test data
      if (CONFIG.testMode) {
        console.log('[CustomSearch] 🧪 TEST MODE - Using test data with filters');
        return this.getTestData(query);
      }

      // PRODUCTION MODE: Call API with filters
      console.log('[CustomSearch] Calling API with filters...');

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CONFIG.timeout);

      // Build request body with filters in new format
      const requestBody = {
        query: query,
        url: window.location.href,
        timestamp: new Date().toISOString(),
        language: Shopify.locale || 'en',
        currency: Shopify.country || 'US',
        sortBy: sortBy || 'relevance',
        filters: filters // Send filters object with param_name keys
      };

      return fetch(CONFIG.apiEndpoint, {
        method: 'POST',
        headers: {
          'Accept': '*/*',
          'Accept-Encoding': 'gzip, deflate, br, zstd',
          'Accept-Language': 'en-US,en;q=0.9',
          'Content-Type': 'application/json',
          'Origin': window.location.origin,
          'Referer': window.location.href,
          'User-Agent': navigator.userAgent,
          'ngrok-skip-browser-warning': '1'
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      })
        .then(response => {
          clearTimeout(timeoutId);
          if (!response.ok) {
            throw new Error(`API error: ${response.status}`);
          }
          return response.json();
        })
        .catch(error => {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            throw new Error('API timeout');
          }
          throw error;
        });
    },

    /**
     * Initialize mobile filter drawer (matches Shopify's theme.Drawers functionality)
     */
    initMobileFilterDrawer: function () {
      console.log('[CustomSearch] Initializing mobile filter drawer...');

      // Check if Shopify's theme.Drawers is available
      if (typeof theme !== 'undefined' && theme.Drawers) {
        // Reinitialize Shopify's FilterDrawer for custom search results
        if (theme.FilterDrawer) {
          console.log('[CustomSearch] Reinitializing existing theme.FilterDrawer');
          theme.FilterDrawer.init();
        } else {
          console.log('[CustomSearch] Creating new theme.FilterDrawer');
          theme.FilterDrawer = new theme.Drawers('FilterDrawer', 'collection-filters', true);
        }
        return;
      }

      // Fallback: Manual implementation if theme.Drawers not available
      console.warn('[CustomSearch] theme.Drawers not available, using manual drawer implementation');

      // Scope selectors to custom search results
      const customSearchContainer = document.getElementById('customSearchResults');
      if (!customSearchContainer) {
        console.warn('[CustomSearch] Custom search container not found');
        return;
      }

      const drawer = customSearchContainer.querySelector('#FilterDrawer');
      const openBtn = customSearchContainer.querySelector('.js-drawer-open-collection-filters');
      const closeBtn = drawer ? drawer.querySelector('.js-drawer-close') : null;

      if (!drawer || !openBtn) {
        console.warn('[CustomSearch] Drawer elements not found', { drawer: !!drawer, openBtn: !!openBtn });
        return;
      }

      console.log('[CustomSearch] Drawer elements found successfully');

      let isOpen = false;

      // Open drawer function
      function openDrawer(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        if (isOpen) return;

        console.log('[CustomSearch] Opening filter drawer');

        // Add classes to show drawer
        drawer.classList.add('drawer--is-open');
        document.documentElement.classList.add('js-drawer-open');
        openBtn.setAttribute('aria-expanded', 'true');
        isOpen = true;

        // Close on outside click
        setTimeout(() => {
          document.addEventListener('click', handleOutsideClick);
        }, 100);

        // Close on ESC key
        document.addEventListener('keydown', handleEscKey);

        // Lock body scroll on mobile
        document.body.style.overflow = 'hidden';
      }

      // Close drawer function
      function closeDrawer(e) {
        if (e) {
          e.preventDefault();
        }

        if (!isOpen) return;

        console.log('[CustomSearch] Closing filter drawer');

        // Remove classes to hide drawer
        drawer.classList.remove('drawer--is-open');
        drawer.classList.add('js-drawer-closing');
        document.documentElement.classList.remove('js-drawer-open');
        openBtn.setAttribute('aria-expanded', 'false');
        isOpen = false;

        // Remove event listeners
        document.removeEventListener('click', handleOutsideClick);
        document.removeEventListener('keydown', handleEscKey);

        // Unlock body scroll
        document.body.style.overflow = '';

        // Remove closing class after animation
        setTimeout(() => {
          drawer.classList.remove('js-drawer-closing');
        }, 300);
      }

      // Handle outside clicks
      function handleOutsideClick(e) {
        if (!drawer.contains(e.target) && !openBtn.contains(e.target)) {
          closeDrawer();
        }
      }

      // Handle ESC key
      function handleEscKey(e) {
        if (e.keyCode === 27 || e.key === 'Escape') {
          closeDrawer();
        }
      }

      // Bind open button
      openBtn.addEventListener('click', openDrawer);

      // Bind close button
      if (closeBtn) {
        closeBtn.addEventListener('click', closeDrawer);
      }

      console.log('[CustomSearch] Mobile filter drawer initialized');
    },

    /**
     * Fallback to Shopify default search
     */
    fallbackToShopify: function () {
      console.log('%c[CustomSearch] 🏪 SHOPIFY SEARCH ACTIVE - Showing native results', 'background: #96BF48; color: white; padding: 5px; font-weight: bold;');

      // Show Shopify results
      const shopifyResults = document.getElementById('shopifySearchResults');
      if (shopifyResults) {
        console.log('[CustomSearch] Found Shopify results container, making visible');
        shopifyResults.style.display = 'block';
      } else {
        console.warn('[CustomSearch] Shopify results container not found!');
      }
    },

    // Helper to extract product IDs from Shopify search results
    getShopifyProductIds: function () {
      const idsSet = new Set();
      const container = document.getElementById('shopifySearchResults');
      if (container) {
        container.querySelectorAll('[data-product-id]').forEach(el => {
          const id = el.getAttribute('data-product-id');
          if (id) idsSet.add(String(id));
        });
      }
      return Array.from(idsSet);
    }

  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      CustomSearch.init();
    });
  } else {
    CustomSearch.init();
  }

  // Expose for debugging
  window.CustomSearch = CustomSearch;

})();

/*
--------------------------- SEARCH-AI ANALYTICS ---------------------------
*/

function reportSearchToAnalytics(res, search_query, search_group) {
  if (window.SearchAI) {
    // Handle both formats: [{id: '123'}] or ['123']
    const productIds = res?.results?.map(item => String(item?.id || item)) || [];

    const searchEvent = {
      type: "search",
      query: search_query,
      search_id: SearchAI_SearchId,
      product_ids: productIds,
      session_id: SearchAI.Session.getSessionId?.(),
      client_id: SearchAI.Client.getClientId?.(),
      timestamp: Date.now()
    };

    // Add search_group if provided (for overriding)
    if (search_group !== undefined) {
      searchEvent.search_group = search_group;
    }

    SearchAI.Analytics?.report?.(searchEvent);
  }
}

// General "Add to Cart" listener for Shopify native grid blocks / modals
document.addEventListener("click", function (e) {
  const atcButton = e.target.closest('.add-to-cart');
  if (!atcButton || atcButton.disabled) return;

  // 1. Find the local container for the ID (this is usually close to the button)
  const localContainer = e.target.closest('.product-block, .custom-quick-add-modal');

  // 2. Find the "Main" container that holds BOTH the price block and the button block
  const mainWrapper = e.target.closest('[data-product-blocks], .grid-product__content, .custom-quick-add-modal');

  if (!localContainer || !mainWrapper) return;

  let productId = null;
  let price = 0;
  const currency = window.Shopify?.currency?.active || "EUR";

  // --- GET PRODUCT ID ---
  const idInput = localContainer.querySelector('input[name="product-id"]');
  const jsonDataInput = localContainer.querySelector('input[data-product-data]');

  if (idInput) {
    productId = idInput.value;
  } else if (jsonDataInput) {
    try {
      productId = JSON.parse(jsonDataInput.getAttribute('data-product-data')).id;
    } catch (e) { }
  }

  // --- GET PRICE (Looking globally within the main wrapper) ---
  // This looks for the price block wherever it is in the modal/grid
  const priceElement = mainWrapper.querySelector('[data-product-price]');

  if (priceElement) {
    const rawPrice = priceElement.textContent.replace(/[^\d,.]/g, '').replace(',', '.');
    price = parseFloat(rawPrice);
  } else if (jsonDataInput) {
    // Fallback to JSON price if the HTML span isn't found
    try {
      price = JSON.parse(jsonDataInput.getAttribute('data-product-data')).price / 100;
    } catch (e) { }
  }

  // 3. Report
  if (productId && typeof SearchAI !== 'undefined' && SearchAI.Analytics) {
    const addToCartEvent = {
      type: "add_to_cart",
      product_id: String(productId),
      price: price || 0,
      currency: currency,
      session_id: SearchAI.Session.getSessionId?.(),
      client_id: SearchAI.Client.getClientId?.(),
      timestamp: Date.now()
    };

    console.log("🚀 Tracked Native ATC:", addToCartEvent);
    SearchAI.Analytics.report(addToCartEvent);
  }
});

document.addEventListener("click", async function (e) {
  const clickedLink = e.target.closest("a");
  if (!clickedLink) return;

  const item = clickedLink.closest(".grid__item");
  if (!item) return;

  const productLink = e.target.closest("a.grid-product__link, .grid-product__content a");
  if (!productLink) return;

  // ⛔ Prevent the default redirect
  e.preventDefault();

  const title = item.querySelector(".grid-product__title")?.textContent?.trim() || "";
  const url = productLink.href;

  const handle = (url.match(/\/products\/([^/?#]+)/) || [])[1] || "";
  let productId = null;
  try {
    const res = await fetch(`/products/${handle}.js`);
    if (res.ok) {
      const product = await res.json();
      productId = String(product.id);
    }
  } catch (error) {
    console.error('searchai Error fetching product ID:', error);
  }

  const params = new URLSearchParams(window.location.search);
  const query = params.get("q") || "";

  const clickEvent = {
    type: "product_click",
    query,
    product_id: productId,
    search_id: SearchAI_SearchId,
    product_title: title,
    url,
    session_id: SearchAI.Session.getSessionId?.(),
    client_id: SearchAI.Client.getClientId?.(),
    timestamp: Date.now()
  };

  if (!CustomSearch.isErrorFallback) {
    SearchAI.Analytics?.report?.(clickEvent);
  } else {
    console.log('[CustomSearch] ℹ️ Click analytics skipped due to error fallback state');
  }

  // → After reporting analytics, manually redirect
  window.location.href = url;
});



/**
 * Attach analytics tracking to desktop quick shop modal form
 * @param {HTMLElement} btn - The quick shop button element
 * @param {string} handle - Product handle
 * @param {string} productId - Product ID
 */
function attachDesktopQuickShopAnalytics(btn, handle, productId) {
  // Attach SearchAI analytics tracking when the button is clicked (modal opens)
  // This ensures the modal content is fully loaded before we try to find the form
  btn.addEventListener('click', function () {
    console.log('🔵 [DEBUG] Desktop quick shop button clicked for product:', handle);

    // Wait for modal to open and render - increased to 1500ms for slower connections
    setTimeout(() => {
      const modalHolder = document.getElementById(`QuickShopHolder-${handle}`);
      //console.log('🔵 [DEBUG] Modal holder found:', !!modalHolder, 'ID:', `QuickShopHolder-${handle}`);

      if (modalHolder) {
        // Debug: log modal contents
        //console.log('🔵 [DEBUG] Modal HTML length:', modalHolder.innerHTML.length);
        //console.log('🔵 [DEBUG] Modal has forms:', modalHolder.querySelectorAll('form').length);

        // Try multiple selectors for the form
        let addToCartForm = modalHolder.querySelector('form[action*="/cart/add"]');
        if (!addToCartForm) {
          addToCartForm = modalHolder.querySelector('form[action="/cart/add"]');
        }
        if (!addToCartForm) {
          addToCartForm = modalHolder.querySelector('form.product-form');
        }
        if (!addToCartForm) {
          addToCartForm = modalHolder.querySelector('form');
        }

        console.log('🔵 [DEBUG] Form found with selector:', addToCartForm ? addToCartForm.getAttribute('action') : 'NONE');

        if (addToCartForm) {
          //console.log('🔵 [DEBUG] Found add to cart form in modal for product:', productId);

          // Check if already tracked to avoid duplicates
          if (addToCartForm.dataset.searchaiTracked === 'true') {
            //console.log('🔵 [DEBUG] Form already has tracking attached');
            return;
          }

          addToCartForm.dataset.searchaiTracked = 'true';
          //console.log('🔵 [DEBUG] Attaching analytics to desktop quick shop form for product:', productId);

          addToCartForm.addEventListener('submit', function (e) {
            //console.log('🔵 [DEBUG] Desktop quick shop form submitted for product:', productId);

            if (window.SearchAI && window.SearchAI.Analytics) {
              const variantIdInput = addToCartForm.querySelector('[name="id"]');
              const variantId = variantIdInput?.value;

              // Get price from the modal
              const priceElement = modalHolder.querySelector('.price-item--regular, .price__regular .price-item, [data-product-price]');
              const price = priceElement?.textContent?.trim() || '0';

              const currencyMeta = document.querySelector('meta[property="og:price:currency"]') ||
                document.querySelector('meta[property="og:price:currency:code"]');
              const currency = currencyMeta?.content || 'EUR';

              const addToCartEvent = {
                type: "add_to_cart",
                product_id: productId,
                variant_id: variantId,
                price: price,
                currency: currency,
                session_id: SearchAI.Session?.getSessionId?.(),
                client_id: SearchAI.Client?.getClientId?.(),
                timestamp: Date.now(),
                source: "search_desktop_quick_shop"
              };

              //console.log("✅ Desktop Quick Shop - Add to cart (Search Results):", addToCartEvent);
              if (!CustomSearch.isErrorFallback) {
                SearchAI.Analytics.report(addToCartEvent);
              } else {
                console.log('[CustomSearch] ℹ️ Add to cart analytics skipped due to error fallback state');
              }
            } else {
              //console.warn('⚠️ SearchAI not available for desktop quick shop tracking');
            }
          });
        } else {
          console.warn('[CustomSearch] Add to cart form not found in modal for:', handle);
          console.warn('[CustomSearch] Available forms:', Array.from(modalHolder.querySelectorAll('form')).map(f => f.getAttribute('action')));
        }
      } else {
        console.warn('[CustomSearch] Modal holder not found:', `QuickShopHolder-${handle}`);
      }
    }, 1500); // Increased to 1500ms for modal content to fully render
  });
}
/*
--------------------------- SEARCH-AI ANALYTICS ---------------------------
*/